import React, { useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { Spin, notification } from 'antd';
import moment from 'moment';
import logger, { Logger } from '../../utils/logger';
import { checkGoogleTokenStatus, getGoogleTokens, disconnectGoogleScope } from '../../services/integrations';

interface GoogleCalendarIntegrationProps {
  userId: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const GoogleCalendarIntegration: React.FC<GoogleCalendarIntegrationProps> = ({
  userId,
  onSuccess,
  onError,
}) => {
  logger.log("🚀 CALENDAR COMPONENT: Component mounting...");
  logger.log("👤 CALENDAR COMPONENT: User ID:", userId);
  
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [connectedDate, setConnectedDate] = useState<Date | null>(null);

  const checkCalendarStatus = async () => {
    logger.log("🔍 CALENDAR COMPONENT: Starting Calendar status check...");
    try {
      logger.log("📡 CALENDAR COMPONENT: Calling checkGoogleTokenStatus service...");
      const response = await checkGoogleTokenStatus();
      logger.log("✅ CALENDAR COMPONENT: Service response received:", response.data);
      
      const data = response.data;
      Logger.log('Calendar Status Response:', data);

      if (data.status && data.data) {
        logger.log("📋 CALENDAR COMPONENT: Checking Calendar scope in response...");
        const hasCalendarScope = data.data.scope?.includes('https://www.googleapis.com/auth/calendar.events.owned');
        const isActive = data.data.status === 'active' && hasCalendarScope;
        
        logger.log("🔍 CALENDAR COMPONENT: Scope analysis:", {
          fullScope: data.data.scope,
          hasCalendarScope,
          status: data.data.status,
          isActive
        });
        
        setIsConnected(isActive);
        
        if (isActive && data.data.connectedAt) {
          logger.log("📅 CALENDAR COMPONENT: Setting connected date:", data.data.connectedAt);
          setConnectedDate(new Date(data.data.connectedAt));
        }
        
        logger.log("✅ CALENDAR COMPONENT: Calendar is", isActive ? "CONNECTED" : "NOT CONNECTED");
      } else {
        logger.log("❌ CALENDAR COMPONENT: Invalid response structure");
        setIsConnected(false);
      }
    } catch (error) {
      logger.error('💥 CALENDAR COMPONENT: Error checking calendar status:', error);
      setIsConnected(false);
    } finally {
      logger.log("🔄 CALENDAR COMPONENT: Setting loading to false");
      setLoading(false);
    }
  };

  useEffect(() => {
    logger.log("🔄 CALENDAR COMPONENT: useEffect triggered - starting status check");
    checkCalendarStatus();
  }, []);

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/calendar.events.owned',
    onSuccess: async (codeResponse) => {
      logger.log("🎉 CALENDAR COMPONENT: Google OAuth success!");
      logger.log("🔑 CALENDAR COMPONENT: Authorization code received:", codeResponse.code?.substring(0, 20) + "...");
      
      try {
        setLoading(true);
        logger.log("📡 CALENDAR COMPONENT: Calling getGoogleTokens service...");
        
        const response = await getGoogleTokens(codeResponse.code);
        logger.log("✅ CALENDAR COMPONENT: Token exchange response:", response.data);
        
        const data = response.data;
        if (data.status) {
          logger.log("🔄 CALENDAR COMPONENT: Token exchange successful, rechecking status...");
          await checkCalendarStatus();
          
          notification.success({
            message: 'Calendar connected successfully!'
          });
          logger.log("🎉 CALENDAR COMPONENT: Calling onSuccess callback");
          onSuccess();
          setConnectedDate(new Date());
        } else {
          logger.log("❌ CALENDAR COMPONENT: Token exchange failed:", data);
        }
      } catch (error) {
        logger.error('💥 CALENDAR COMPONENT: Google login failed:', error);
        notification.error({
          message: 'Connection failed'
        });
        onError('Connection failed');
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      logger.error('💥 CALENDAR COMPONENT: Google OAuth error:', error);
    }
  });

  const handleDisconnect = async () => {
    logger.log("🔌 CALENDAR COMPONENT: Starting disconnect process...");
    try {
      setLoading(true);
      logger.log("📡 CALENDAR COMPONENT: Calling disconnectGoogleScope service...");
      
      const response = await disconnectGoogleScope('https://www.googleapis.com/auth/calendar.events.owned');
      logger.log("✅ CALENDAR COMPONENT: Disconnect response:", response.data);
      
      const data = response.data;
      if (data.status) {
        logger.log("✅ CALENDAR COMPONENT: Disconnect successful");
        setIsConnected(false);
        setConnectedDate(null);
        
        notification.success({
          message: 'Calendar disconnected successfully!'
        });
        logger.log("🎉 CALENDAR COMPONENT: Calling onSuccess callback");
        onSuccess();
      } else {
        logger.log("❌ CALENDAR COMPONENT: Disconnect failed:", data);
      }
    } catch (error) {
      logger.error('💥 CALENDAR COMPONENT: Disconnect failed:', error);
      notification.error({
        message: 'Failed to disconnect Calendar'
      });
      onError('Disconnect failed');
    } finally {
      setLoading(false);
    }
  };

  logger.log("🎨 CALENDAR COMPONENT: Rendering with state:", { loading, isConnected, connectedDate });

  return (
    <div style={{ 
      width: '100%',
      maxWidth: '600px',
      margin: '40px auto',
      padding: '30px',
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      textAlign: 'center'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '30px'
      }}>
        <img 
          src={require('../../assets/images/integration/googlecalender.png')}
          alt="Google Calendar"
          style={{
            width: '28px',
            height: '28px',
            marginRight: '15px'
          }}
        />
        <h2 style={{ 
          margin: 0,
          fontSize: '24px',
          color: '#333'
        }}>
          Google Calendar
        </h2>
      </div>

      {loading ? (
        <div>
          <Spin />
          <p style={{ marginTop: '20px', color: '#666' }}>
            Checking Calendar connection status...
          </p>
        </div>
      ) : isConnected ? (
        <div>
          <div style={{ 
            marginBottom: '20px', 
            color: '#52c41a',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <span style={{ color: '#52c41a' }}>✓</span>
            Your Google Calendar is connected and working properly
          </div>
          
          <div style={{ 
            color: '#666',
            fontSize: '15px',
            marginBottom: '20px'
          }}>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              textAlign: 'left',
              margin: '0 auto',
              maxWidth: '400px'
            }}>
              <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#4285F4', marginRight: '10px' }}>•</span>
                All your tasks sync automatically with Google Calendar
              </li>
              <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#4285F4', marginRight: '10px' }}>•</span>
                Create calendar events directly from 3sigma
              </li>
              <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#4285F4', marginRight: '10px' }}>•</span>
                Schedule meetings and manage appointments seamlessly
              </li>
            </ul>
          </div>
          
          {connectedDate && (
            <p style={{ 
              color: '#666',
              fontSize: '14px',
              marginTop: '20px'
            }}>
              Calendar connected successfully on {moment(connectedDate).format("DD MMM YYYY hh:mm A")}
            </p>
          )}
          
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button
              onClick={handleDisconnect}
              style={{
                padding: '8px 16px',
                backgroundColor: 'transparent',
                color: '#4285F4',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Disconnect Calendar
            </button>
          </div>
        </div>
      ) : (
        <div>
          <p style={{ 
            marginBottom: '25px',
            color: '#666',
            fontSize: '15px'
          }}>
            Connect your Google Calendar to sync your tasks automatically and manage your schedule seamlessly within 3sigma CRM.
          </p>
          
          <button
            onClick={() => {
              logger.log("🔘 CALENDAR COMPONENT: Connect button clicked");
              googleLogin();
            }}
            style={{
              padding: '12px 24px',
              backgroundColor: '#4285F4',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
          >
            Connect with Google Calendar
          </button>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarIntegration;